/**
 * useProposalKanbanCounts - Totalizadores por coluna do Kanban de Propostas
 *
 * Uma única RPC devolve contagem, soma de valor e soma de MRR de todas as
 * colunas, evitando 9 consultas separadas de cabeçalho.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { buildProposalRpcParams, type ProposalFilters } from '@/hooks/useProposalKanbanColumn';
import type { ProposalStatus } from '@/hooks/useDealProposals';

const logger = createLogger('ProposalKanbanCounts');

/** Linha retornada pela RPC */
interface ProposalKanbanCountRow {
  status: ProposalStatus;
  total_count: number;
  total_value: number;
  total_mrr: number;
}

/** Totais de uma coluna */
export interface ProposalKanbanColumnTotals {
  count: number;
  value: number;
  mrr: number;
}

/**
 * Retorna um Map situação -> { count, value, mrr } com os mesmos filtros das colunas.
 */
export function useProposalKanbanCounts(filters?: ProposalFilters, enabled: boolean = true) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['proposal-kanban-counts', currentTenant?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_proposals_kanban_counts', {
        ...buildProposalRpcParams(filters),
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar contagens do Kanban de Propostas:', error.message);
        throw error;
      }

      const map = new Map<ProposalStatus, ProposalKanbanColumnTotals>();
      ((data || []) as unknown as ProposalKanbanCountRow[]).forEach((row) => {
        map.set(row.status, {
          count: Number(row.total_count) || 0,
          value: Number(row.total_value) || 0,
          mrr: Number(row.total_mrr) || 0,
        });
      });
      return map;
    },
    enabled: !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60_000,
  });

  return {
    countsMap: query.data ?? new Map<ProposalStatus, ProposalKanbanColumnTotals>(),
    isLoading: query.isLoading,
  };
}
